import { BillingApiError } from '../api-client'
import { billingDeclineMessage, billingErrorMessage } from '../billing-error-message'
import type { CheckoutOutcome } from './checkout-types'

/** The fields of a settled TapPay charge response the panel reacts to. Shared
 *  by subscribe / guest / credit-pack (their responses are structurally this). */
export interface ChargeResultShape {
  requires3ds?: boolean
  paymentUrl?: string
  tradeId?: number
  /** A re-subscribe/double-submit that moved no money — success UX, no tradeId. */
  deduplicated?: boolean
  /** Product-specific authoritative subscription returned on a deduplicated checkout. */
  subscription?: unknown
  error?: string | null
  errorCode?: string | null
}

type Translate = (key: string) => string

/** The server returns 400 `{ error: 'card_required' }` when the wallet no longer
 *  covers the whole price (balance changed since the client computed the split). */
export function isCardRequiredError(cause: unknown): boolean {
  return (
    cause instanceof BillingApiError &&
    (cause.body as { error?: string } | null)?.error === 'card_required'
  )
}

/** The server returns 422 `{ error: 'coupon_invalid', reason? }` when a supplied
 *  coupon can't apply (e.g. it lapsed between preview and submit, or it is bound
 *  to another account). */
export function isCouponInvalidError(cause: unknown): cause is BillingApiError {
  return (
    cause instanceof BillingApiError &&
    (cause.body as { error?: string } | null)?.error === 'coupon_invalid'
  )
}

function couponInvalidReason(cause: BillingApiError): string | undefined {
  const reason = (cause.body as { reason?: unknown } | null)?.reason
  return typeof reason === 'string' && reason.length > 0 ? reason : undefined
}

const PAYMENT_RECOVERY_CODES = new Set([
  'payment_recovery_pending',
  'checkout_intent_conflict',
  'pending_3ds_conflict',
])

/** These 409s mean the bank outcome is still being reconciled. The buyer must
 * not retry, and analytics must not count this as a declined/failed purchase. */
export function isPaymentRecoveryError(cause: unknown): boolean {
  if (!(cause instanceof BillingApiError)) return false
  const error = (cause.body as { error?: unknown } | null)?.error
  return typeof error === 'string' && PAYMENT_RECOVERY_CODES.has(error)
}

export function paymentRecoveryTradeId(cause: unknown): number | null {
  if (!isPaymentRecoveryError(cause)) return null
  const tradeId = Number(
    (cause as BillingApiError).body &&
      ((cause as BillingApiError).body as { tradeId?: unknown }).tradeId
  )
  return Number.isInteger(tradeId) && tradeId > 0 ? tradeId : null
}

/** The message of a refusal that must lock the form, or null. */
function lockedRefusalMessageKey(cause: unknown): string | null {
  if (!(cause instanceof BillingApiError)) return null
  const body = cause.body as { error?: unknown; errorCode?: unknown } | null
  if (body?.error === 'previous_payment_refunding') return 'billing.payError.previousRefunding'
  if (body?.errorCode === 'payment_needs_support' || body?.error === 'payment_needs_support') {
    return 'billing.payError.needsSupport'
  }
  return null
}

/**
 * Maps a settled charge RESPONSE (the 200 shape) to a normalized outcome. The
 * single source of truth for the 3DS / decline / success classification shared
 * by every checkout product. Guest `requiresSignIn` is checked by the caller
 * BEFORE this (it's guest-only and short-circuits without a charge).
 */
export function outcomeFromChargeResult(
  result: ChargeResultShape,
  t: Translate,
  fallbackKey: string
): CheckoutOutcome {
  if (result.requires3ds) {
    // A 3DS challenge with no paymentUrl can't be completed — surface it as an
    // error rather than falling through to a phantom success.
    return result.paymentUrl
      ? { kind: 'redirect', url: result.paymentUrl }
      : { kind: 'error', message: billingErrorMessage(undefined, t, fallbackKey) }
  }
  // A declined card can arrive as a 200-with-error today; map it so it can never
  // read as success.
  if (result.errorCode || result.error) {
    return { kind: 'declined', message: billingDeclineMessage(result, t, fallbackKey) }
  }
  // Only a REAL charge (tradeId present) should fire a Purchase conversion; the
  // tradeId is carried through so the strategy can gate analytics on it.
  return {
    kind: 'success',
    ...(result.tradeId !== undefined ? { tradeId: result.tradeId } : {}),
    ...(result.deduplicated ? { deduplicated: true } : {}),
    ...(result.subscription !== undefined ? { subscription: result.subscription } : {}),
  }
}

/**
 * Maps a THROWN charge error to a normalized outcome. The two universal control
 * codes (coupon_invalid / card_required) get their own outcomes so the panel can
 * react identically across products; anything else is a generic inline error.
 */
export function outcomeFromChargeError(
  cause: unknown,
  t: Translate,
  fallbackKey: string
): CheckoutOutcome {
  if (isCouponInvalidError(cause)) {
    const reason = couponInvalidReason(cause)
    return reason ? { kind: 'couponInvalid', reason } : { kind: 'couponInvalid' }
  }
  if (isCardRequiredError(cause)) return { kind: 'cardRequired' }
  // Two refusals where paying again is the wrong move until a person or a
  // refund settles the earlier payment: lock the form like a payment being
  // confirmed, with a message that says which.
  const lockedKey = lockedRefusalMessageKey(cause)
  if (lockedKey) return { kind: 'recoveryPending', message: t(lockedKey) }
  if (isPaymentRecoveryError(cause)) {
    return { kind: 'recoveryPending', message: t('billing.payError.recoveryPending') }
  }
  return { kind: 'error', message: billingErrorMessage(cause, t, fallbackKey) }
}
